import { useMemo } from 'react';
import { UseFormReturn, useWatch } from 'react-hook-form';

import { MultiSelect } from 'components/MultiSelect';
import { Flex } from 'components/layout';
import { Typography } from 'components/typography';
import { LTLAccessorial } from 'pages/QuoteView/Quoting/RequestQuickQuote/types';
import { AccessorialItem, NormalizedLoad } from 'types/Load';

function getAccessorialId(accessorial: LTLAccessorial): string {
  return accessorial.externalId != null ? String(accessorial.externalId) : '';
}

export function AccessorialsForm({
  formMethods,
  accessorials,
  isLoading,
}: {
  formMethods: UseFormReturn<NormalizedLoad>;
  accessorials: LTLAccessorial[];
  isLoading: boolean;
}) {
  const { control, setValue } = formMethods;

  const selectedAccessorials =
    useWatch({
      control,
      name: 'accessorials',
      defaultValue: [],
    }) ?? [];

  const orderedAccessorials = useMemo(() => {
    const favorites = accessorials.filter(
      (accessorial) => accessorial.isFavorite
    );
    const nonFavorites = accessorials.filter(
      (accessorial) => !accessorial.isFavorite
    );
    return favorites.concat(nonFavorites);
  }, [accessorials]);

  const options = useMemo(
    () =>
      orderedAccessorials
        .map((accessorial) => ({
          label: accessorial.name,
          value: getAccessorialId(accessorial),
        }))
        .filter((option) => option.value !== ''),
    [orderedAccessorials]
  );

  const selectedValues = useMemo(
    () =>
      selectedAccessorials
        .map((accessorial: AccessorialItem) =>
          accessorial.id != null ? String(accessorial.id) : ''
        )
        .filter(Boolean),
    [selectedAccessorials]
  );

  function handleChange(nextSelectedValues: string[]) {
    const nextAccessorials = nextSelectedValues
      .map((externalId) => {
        const accessorial = orderedAccessorials.find(
          (option) => getAccessorialId(option) === externalId
        );

        if (!accessorial) {
          return null;
        }

        return {
          id: accessorial.externalId,
          displayText: accessorial.name,
        } satisfies AccessorialItem;
      })
      .filter(Boolean) as AccessorialItem[];

    setValue('accessorials', nextAccessorials, {
      shouldDirty: true,
      shouldTouch: true,
    });
  }

  return (
    <Flex direction='col' gap='lg' className='w-full'>
      <Typography
        variant='h5'
        className='text-neutral-800 font-semibold whitespace-nowrap'
      >
        Accessorials
      </Typography>

      {isLoading ? (
        <div className='text-sm text-neutral-500 text-center py-4'>
          Loading accessorials...
        </div>
      ) : accessorials.length === 0 ? (
        <div className='text-sm text-neutral-500 text-center py-4'>
          No accessorials available
        </div>
      ) : (
        <MultiSelect
          placeholder='Select accessorials...'
          value={selectedValues}
          onChange={handleChange}
          options={options}
          className='w-full'
          displayMode='count'
        />
      )}
    </Flex>
  );
}
